import React, { useState } from 'react';
import { Modal, Pressable, ScrollView, View, useWindowDimensions } from 'react-native';
import {
  fmt, formatClockTime, formatDate, invoiceHtml, isParcel, lineName, useAppStore,
} from '@restorant/core';
import type { CartLine, Order } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useCatLabel, useLang, useT, useTableLabel } from '../i18n';
import { useSettings } from '../menu';
import { printHtml } from '../print';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/Icon';
import { T } from './Text';

/**
 * Prints the bill for one order.
 *
 * The invoice is built from the order's own stored lines, never from the live
 * menu, so a reprint months later shows exactly what was charged even if the
 * dish has been re-priced or deleted since.
 */
function PrintButton({ order }: { order: Order }) {
  const colors = useColors();
  const t = useT();
  const lang = useLang();
  const settings = useSettings();
  const tableLabel = useTableLabel();
  const showToast = useAppStore((s) => s.showToast);
  const [busy, setBusy] = useState(false);

  const print = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await printHtml(
        invoiceHtml({ order, settings, tableLabel: tableLabel(order.tableId), lang }),
      );
    } catch {
      // Native without expo-print installed, or a blocked print dialog.
      showToast(t('print_failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Pressable
      onPress={print}
      testID="print-bill"
      accessibilityLabel={t('print_bill')}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 9,
        paddingHorizontal: 14,
        borderRadius: radius.md,
        backgroundColor: colors.gold,
        opacity: pressed || busy ? 0.8 : 1,
      })}
    >
      <Icon name="printer" size={16} color={colors.onGold} />
      <T size={13} weight={800} color={colors.onGold}>
        {busy ? t('printing') : t('print')}
      </T>
    </Pressable>
  );
}

function MetaRow({ icon, label, value }: { icon: IconName; label: string; value: string }) {
  const colors = useColors();
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, flexGrow: 1, flexBasis: 150 }}>
      <Icon name={icon} size={15} color={colors.textFaint} />
      <View style={{ gap: 1, flexShrink: 1 }}>
        <T size={10.5} weight={600} color={colors.textFaint} style={{ letterSpacing: 0.4 }}>
          {label.toUpperCase()}
        </T>
        <T size={13} weight={600} numberOfLines={1}>
          {value}
        </T>
      </View>
    </View>
  );
}

/**
 * True when the receipt is too narrow for four aligned columns.
 *
 * On a 375px phone the fixed columns (qty 36 + rate 82 + amount 80, plus the
 * gaps) left about 79px for the dish, so "Manchurian Noodles" broke mid-word
 * into "Manchuri / an / Noodles". Below this width the line stacks instead.
 */
function useNarrowReceipt(): boolean {
  const { width } = useWindowDimensions();
  return width < 560;
}

function LineRow({ line }: { line: CartLine }) {
  const colors = useColors();
  const catLabel = useCatLabel();
  const lang = useLang();
  const narrow = useNarrowReceipt();
  const amount = line.price * line.qty;

  const sub = catLabel(line.cat) + (line.variant !== 'Price' ? ` · ${line.variant}` : '');

  // Phone layout: the dish gets the full width on its own line, and the
  // arithmetic sits under it as one readable sentence — "×3 · ₹149" — rather
  // than as columns too narrow to hold a word.
  if (narrow) {
    return (
      <View
        style={{
          paddingVertical: 11,
          gap: 4,
          borderBottomWidth: 1,
          borderBottomColor: colors.line,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
          <T size={13.5} weight={600} style={{ flex: 1 }}>
            {lineName(line, lang)}
          </T>
          <T size={13.5} weight={700}>
            {fmt(amount)}
          </T>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <T size={11} color={colors.textFaint} style={{ flex: 1 }} numberOfLines={1}>
            {sub}
          </T>
          {line.offerPct > 0 ? (
            <T
              size={11}
              color={colors.textFaint}
              style={{ textDecorationLine: 'line-through' }}
            >
              {fmt(line.basePrice)}
            </T>
          ) : null}
          <T size={11.5} color={colors.textDim}>
            ×{line.qty} · {fmt(line.price)}
          </T>
        </View>
      </View>
    );
  }

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: colors.line,
      }}
    >
      <View style={{ flex: 1, gap: 3, minWidth: 0 }}>
        <T size={13.5} weight={600}>
          {lineName(line, lang)}
        </T>
        <T size={11} color={colors.textFaint}>
          {catLabel(line.cat)}
          {line.variant !== 'Price' ? ` · ${line.variant}` : ''}
        </T>
      </View>

      <T size={12.5} color={colors.textDim} style={{ width: 36, textAlign: 'center' }}>
        ×{line.qty}
      </T>

      <View style={{ width: 82, alignItems: 'flex-end', gap: 2 }}>
        {line.offerPct > 0 ? (
          <T
            size={10.5}
            color={colors.textFaint}
            style={{ textDecorationLine: 'line-through' }}
          >
            {fmt(line.basePrice)}
          </T>
        ) : null}
        <T size={12.5} color={colors.textDim}>
          {fmt(line.price)}
        </T>
      </View>

      <T size={13.5} weight={700} style={{ width: 80, textAlign: 'right' }}>
        {fmt(amount)}
      </T>
    </View>
  );
}

function TotalLine({
  label,
  value,
  tone = 'normal',
}: {
  label: string;
  value: string;
  tone?: 'normal' | 'good' | 'grand';
}) {
  const colors = useColors();
  const grand = tone === 'grand';
  const color = grand ? colors.gold : tone === 'good' ? colors.good : colors.textDim;
  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingTop: grand ? 12 : 7,
        marginTop: grand ? 6 : 0,
        borderTopWidth: grand ? 1 : 0,
        borderTopColor: colors.line,
      }}
    >
      <T size={grand ? 17 : 13} weight={grand ? 700 : 500} color={color} serif={grand}>
        {label}
      </T>
      <T size={grand ? 17 : 13} weight={grand ? 700 : 600} color={color}>
        {value}
      </T>
    </View>
  );
}

/** The receipt itself, without the modal chrome. Also used by the tests. */
export function OrderReceipt({ order, onClose }: { order: Order; onClose?: () => void }) {
  const colors = useColors();
  const t = useT();
  const tableLabel = useTableLabel();
  const narrow = useNarrowReceipt();
  const who = order.customer.name.trim();
  const phone = order.customer.phone.trim();

  return (
    <View
      style={{
        width: '100%',
        maxWidth: 560,
        // No maxHeight here. It used to be '90%', but the parent had no height
        // of its own, so the percentage resolved against nothing and the card
        // grew to the full height of the bill — a long order ran off the
        // bottom of the phone and the ScrollView below never got a viewport to
        // scroll within. The height is now bounded by the modal root instead,
        // which is a real flex:1 box. See OrderDetailModal at the foot of this
        // file, and components/Screen for the same trap on the tabs.
        flexShrink: 1,
        minHeight: 0,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.line,
        borderRadius: radius.lg,
        overflow: 'hidden',
      }}
    >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              padding: 18,
              borderBottomWidth: 1,
              borderBottomColor: colors.line,
              backgroundColor: colors.surface2,
            }}
          >
            <View style={{ gap: 3, flexShrink: 1 }}>
              <T size={18} weight={700} serif color={colors.gold}>
                {t('order_no', { n: order.orderNo ?? order.localNo })}
              </T>
              <T size={12} color={colors.textFaint}>
                {order.synced ? t('order_details') : t('not_synced')}
              </T>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <PrintButton order={order} />
              {onClose ? (
                <Pressable
                  onPress={onClose}
                  testID="close-receipt"
                  accessibilityLabel={t('close')}
                  style={({ hovered }: any) => ({
                    padding: 9,
                    borderRadius: radius.md,
                    borderWidth: 1,
                    borderColor: hovered ? colors.gold : colors.line,
                  })}
                >
                  <Icon name="close" size={17} color={colors.textDim} />
                </Pressable>
              ) : null}
            </View>
          </View>

          <ScrollView
            style={{ flexShrink: 1, minHeight: 0 }}
            contentContainerStyle={{ padding: 18, gap: 18 }}
          >
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
              <MetaRow
                icon={isParcel(order.tableId) ? 'parcel' : 'table'}
                label={t('table')}
                value={tableLabel(order.tableId)}
              />
              <MetaRow
                icon="clock"
                label={t('placed_at')}
                value={`${formatClockTime(new Date(order.placedAt))} · ${formatDate(new Date(order.placedAt))}`}
              />
              <MetaRow icon="user" label={t('customer_name')} value={who || t('walk_in')} />
              <MetaRow icon="phone" label={t('customer_phone')} value={phone || '—'} />
            </View>

            <View>
              {/* On a phone the lines stack, so column headings would label
                  columns that are not there. Two headings is the honest
                  version of the same row. */}
              <View
                style={{
                  flexDirection: 'row',
                  gap: 10,
                  paddingBottom: 8,
                  borderBottomWidth: 1,
                  borderBottomColor: colors.line,
                }}
              >
                <T size={10.5} weight={700} color={colors.textFaint} style={{ flex: 1, letterSpacing: 0.4 }}>
                  {t('items_label').toUpperCase()}
                </T>
                {narrow ? null : (
                  <>
                    <T size={10.5} weight={700} color={colors.textFaint} style={{ width: 36, textAlign: 'center' }}>
                      {t('qty_short').toUpperCase()}
                    </T>
                    <T size={10.5} weight={700} color={colors.textFaint} style={{ width: 82, textAlign: 'right' }}>
                      {t('rate').toUpperCase()}
                    </T>
                  </>
                )}
                <T
                  size={10.5}
                  weight={700}
                  color={colors.textFaint}
                  style={{ width: 80, textAlign: 'right', letterSpacing: 0.4 }}
                >
                  {t('amount').toUpperCase()}
                </T>
              </View>
              {order.items.map((line, i) => (
                <LineRow key={`${line.itemId}-${line.variant}-${i}`} line={line} />
              ))}
            </View>

            <View>
              <TotalLine label={t('subtotal')} value={fmt(order.subtotal)} />
              {order.itemSavings > 0 ? (
                <TotalLine
                  label={t('item_savings')}
                  value={`−${fmt(order.itemSavings)}`}
                  tone="good"
                />
              ) : null}
              {order.discountAmount > 0 ? (
                <TotalLine
                  label={`${t('discount')} (${order.discountPct}%)`}
                  value={`−${fmt(order.discountAmount)}`}
                  tone="good"
                />
              ) : null}
              <TotalLine label={t('total')} value={fmt(order.total)} tone="grand" />
            </View>
          </ScrollView>
    </View>
  );
}

/** The receipt in a dismissable overlay, driven by `openOrderId` in the store. */
export function OrderDetailModal() {
  const colors = useColors();
  const order = useAppStore((s) =>
    s.openOrderId == null ? null : (s.orders.find((o) => o.id === s.openOrderId) ?? null),
  );
  const close = useAppStore((s) => s.closeOrderDetail);

  if (!order) return <Modal visible={false} transparent />;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={close}>
      {/* A flex:1 box gives the receipt a real height to be bounded by, and
          the backdrop sits behind it as a sibling rather than wrapping it —
          a Pressable around a ScrollView eats the drag on Android, which is
          why the bill would not scroll on a phone. */}
      <View
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <Pressable
          onPress={close}
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: colors.scrim,
          }}
        />
        <OrderReceipt order={order} onClose={close} />
      </View>
    </Modal>
  );
}
